import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Crown, Loader2 } from "lucide-react";
import { leads, formatApiError } from "@/lib/api";

export default function VIPPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Você precisa aceitar receber informações e ofertas.");
      return;
    }
    if (!form.name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
      setError("Preencha nome, email e WhatsApp.");
      return;
    }
    setLoading(true);
    try {
      await leads.create({
        name: form.name.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        consent: form.consent,
      });
      navigate("/vip/obrigada", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-[var(--bg)] px-5 py-8 md:px-12 md:py-14">
      <div className="mx-auto max-w-lg">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-super text-white/60 transition hover:text-white"
          data-testid="vip-back-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.2} />
          Voltar
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.9, 0.2, 1] }}
          className="mt-10"
        >
          <div className="mb-3 flex items-center gap-3">
            <Crown className="h-4 w-4 text-[var(--pink)]" strokeWidth={2.2} />
            <span className="font-mono text-[11px] uppercase tracking-super text-[var(--pink)]">
              Grupo VIP · Julssy
            </span>
          </div>

          <h1 className="font-serif text-5xl leading-[0.9] tracking-tight text-white md:text-6xl">
            Entre para o meu
            <br />
            <span className="italic text-[var(--pink)]">grupo exclusivo.</span>
          </h1>

          <p className="mt-5 font-mono text-sm leading-relaxed text-white/70">
            Cadastre-se para receber ofertas, conteúdos inéditos e avisos em primeira mão.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7, ease: [0.2, 0.9, 0.2, 1] }}
          onSubmit={submit}
          className="mt-10 space-y-5"
          data-testid="vip-form"
        >
          <Field label="Nome" htmlFor="name">
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="input"
              placeholder="Como devo te chamar?"
              data-testid="vip-input-name"
              required
            />
          </Field>

          <Field label="Email" htmlFor="email">
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="input"
              placeholder="voce@email.com"
              data-testid="vip-input-email"
              required
            />
          </Field>

          <Field label="WhatsApp" htmlFor="whatsapp">
            <input
              id="whatsapp"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.whatsapp}
              onChange={(e) => update("whatsapp", e.target.value)}
              className="input"
              placeholder="+55 11 99999-9999"
              data-testid="vip-input-whatsapp"
              required
            />
          </Field>

          <label className="flex cursor-pointer items-start gap-3 pt-1">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-white/25 bg-transparent transition-all checked:border-[var(--pink)] checked:bg-[var(--pink)] focus:outline-none focus:ring-2 focus:ring-[var(--pink)]/40"
              data-testid="vip-input-consent"
            />
            <span className="font-mono text-[11px] leading-relaxed text-white/70">
              Concordo em receber informações e ofertas da Julssy por email e WhatsApp.
            </span>
          </label>

          {error && (
            <div
              className="border border-red-400/40 bg-red-400/10 px-3 py-2 font-mono text-[11px] text-red-300"
              data-testid="vip-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-magnetic w-full justify-center disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="vip-submit"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <span>Quero entrar no VIP</span>
                <ArrowUpRight className="arrow h-4 w-4" strokeWidth={2.4} />
              </>
            )}
          </button>

          <p className="text-center font-mono text-[10px] uppercase tracking-super text-white/40">
            seus dados são privados · você pode sair a qualquer momento
          </p>
        </motion.form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-2 block font-mono text-[10px] uppercase tracking-super text-white/50"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
